import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, functions } from '../firebase-config';

const STORAGE_KEY = 'pendingOwnerInvitationId';

function TenantDispatchBrandHeader() {
  const publicBase = process.env.PUBLIC_URL || '';
  const [logoSrc, setLogoSrc] = useState(
    `${publicBase}/tenant-dispatch-icon-512.png`
  );

  return (
    <div
      style={{
        margin: '-40px -40px 28px',
        padding: '28px 40px 24px',
        textAlign: 'center',
        background: 'linear-gradient(180deg, #f1f5f9 0%, #ffffff 100%)',
        borderBottom: '1px solid #e2e8f0',
        borderRadius: '12px 12px 0 0',
      }}
    >
      <img
        src={logoSrc}
        alt="Tenant Dispatch — Automated Property Maintenance Workflow"
        onError={() => {
          setLogoSrc((prev) =>
            prev.includes('tenant-dispatch-icon-192')
              ? prev
              : `${publicBase}/tenant-dispatch-icon-192.png`
          );
        }}
        style={{
          display: 'block',
          margin: '0 auto 14px',
          maxWidth: 'min(420px, 100%)',
          width: 'auto',
          height: 'auto',
          maxHeight: 120,
          objectFit: 'contain',
        }}
      />
      <p
        style={{
          margin: 0,
          fontSize: 14,
          color: '#475569',
          lineHeight: 1.5,
          fontWeight: 500,
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        Invitation from your realtor — connect your owner account to receive applications and manage your properties on our platform.
      </p>
      <p
        style={{
          margin: '12px 0 0',
          fontSize: 12,
          color: '#94a3b8',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        tenantdispatch.com · secure link
      </p>
    </div>
  );
}

const AcceptInvitationPage = () => {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('Checking your session…');
  const [isSuccess, setIsSuccess] = useState(false);
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const [isAccepting, setIsAccepting] = useState(false);
  const acceptAttemptRef = useRef(0);
  const autoAcceptKeyRef = useRef('');

  const authUid = authUser?.uid ?? null;

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setAuthUser);
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!invitationId) {
      setMessage('This invitation link is invalid.');
      setIsSuccess(false);
      return;
    }

    if (!authUid) {
      try {
        sessionStorage.setItem(STORAGE_KEY, invitationId);
      } catch (e) {
        /* ignore */
      }
      autoAcceptKeyRef.current = '';
      setMessage(
        'Sign up or sign in as a property owner using the same email address this invite was sent to. ' +
          'After you log in, we will connect you with your realtor.'
      );
      setIsSuccess(false);
      return;
    }

    const dedupeKey = `${authUid}:${invitationId}`;
    if (autoAcceptKeyRef.current === dedupeKey) {
      return;
    }
    autoAcceptKeyRef.current = dedupeKey;

    const attemptId = ++acceptAttemptRef.current;
    setIsAccepting(true);
    setMessage('Accepting your invitation…');

    const accept = async () => {
      try {
        const acceptOwnerInvite = httpsCallable(functions, 'acceptOwnerInvite');
        await acceptOwnerInvite({ invitationId });

        if (attemptId !== acceptAttemptRef.current) return;

        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          /* ignore */
        }

        setMessage('✓ Invitation accepted! You can now work with this realtor on Tenant Dispatch.');
        setIsSuccess(true);
        setIsAccepting(false);

        setTimeout(() => {
          navigate('/owner');
        }, 2000);
      } catch (error) {
        if (attemptId !== acceptAttemptRef.current) return;
        console.error('Failed to accept invitation:', error);
        autoAcceptKeyRef.current = '';
        const errorMsg = error.message || 'Failed to accept invitation';
        setMessage(`❌ ${errorMsg}`);
        setIsSuccess(false);
        setIsAccepting(false);
      }
    };

    accept();
  }, [invitationId, authUid, navigate]);

  const authHref = `/?ownerInvitationId=${encodeURIComponent(invitationId || '')}#auth`;

  const handleRetry = () => {
    autoAcceptKeyRef.current = '';
    acceptAttemptRef.current += 1;
    setMessage('Accepting your invitation…');
    setIsAccepting(true);
    (async () => {
      const attemptId = acceptAttemptRef.current;
      try {
        const acceptOwnerInvite = httpsCallable(functions, 'acceptOwnerInvite');
        await acceptOwnerInvite({ invitationId });
        if (attemptId !== acceptAttemptRef.current) return;
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
          /* ignore */
        }
        autoAcceptKeyRef.current = `${authUid}:${invitationId}`;
        setMessage('✓ Invitation accepted! You can now work with this realtor on Tenant Dispatch.');
        setIsSuccess(true);
        setIsAccepting(false);
        setTimeout(() => navigate('/owner'), 2000);
      } catch (error) {
        if (attemptId !== acceptAttemptRef.current) return;
        setMessage(`❌ ${error.message || 'Failed to accept invitation'}`);
        setIsSuccess(false);
        setIsAccepting(false);
      }
    })();
  };

  const statusTitle = isSuccess
    ? "You're connected"
    : authUid
      ? 'Confirming your invitation'
      : 'Sign in to accept';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 45%, #ddd6fe 100%)',
        padding: '20px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12)',
          border: '1px solid #e2e8f0',
        }}
      >
        <TenantDispatchBrandHeader />
        <h1
          style={{
            fontSize: '20px',
            margin: '0 0 12px 0',
            color: '#0f172a',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <span aria-hidden style={{ fontSize: 26 }}>
            {isSuccess ? '✓' : authUid ? '⏳' : '📩'}
          </span>
          {statusTitle}
        </h1>
        <p
          style={{
            fontSize: '16px',
            color: isSuccess ? '#065f46' : '#333',
            margin: '0',
            lineHeight: '1.6',
          }}
        >
          {message}
        </p>
        {!authUid && invitationId && (
          <div style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Link
              to={authHref}
              style={{
                display: 'block',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: 'white',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '15px',
                textDecoration: 'none',
              }}
            >
              Continue to sign up or sign in
            </Link>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
              You will be asked to register or log in as a <strong>Home Owner</strong>. Use the inbox email from the
              invite.
            </p>
          </div>
        )}
        {isSuccess && (
          <p style={{ fontSize: '12px', color: '#666', marginTop: '20px' }}>
            Redirecting to your owner dashboard…
          </p>
        )}
        {!isSuccess && authUid && message.startsWith('❌') && !isAccepting && (
          <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                padding: '10px 20px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate('/owner')}
              style={{
                padding: '10px 20px',
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              Go to dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
